import { useEffect, useState } from "react";
import { Star, Quote, Sparkles } from "lucide-react";

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.mindfulme.pusatandalan.com';

interface Testimonial {
  testimonial_id: string;
  user_name: string;
  anonymous: boolean;
  title: string;
  content: string;
  rating: number;
  media_url?: string | null;
  media_type?: 'image' | 'video' | null;
  user?: { name: string; photo?: string | null };
  created_at: string;
}

// Generate initials from name e.g. "Nora Mina" → "NM"
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// Pick a consistent bg color based on name
const AVATAR_COLORS = [
  'bg-purple-500', 'bg-blue-500', 'bg-green-500',
  'bg-pink-500', 'bg-indigo-500', 'bg-teal-500',
];
function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash += name.charCodeAt(i);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

const TestimonialSection = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/testimonials/approved`)
      .then(async (res) => {
        if (!res.ok) throw new Error('Network response was not ok');
        // Remove BOM if present
        const text = await res.text();
        const cleanText = text.replace(/^\uFEFF/, '');
        return JSON.parse(cleanText);
      })
      .then((json) => {
        // API returns { success: true, data: [...] }
        if (!json || !json.success) {
          throw new Error('API response invalid');
        }
        const list: Testimonial[] = Array.isArray(json.data) ? json.data : [];
        setTestimonials(list);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Testimonial fetch error:', err);
        setError(true);
        setLoading(false);
      });
  }, []);

  return (
    <section className="py-20 bg-background relative overflow-hidden">
      {/* Decorative background blobs */}
      <div className="absolute top-20 right-[5%] w-80 h-80 bg-accent/5 rounded-full blur-3xl animate-blob-float-slow"></div>
      <div className="absolute bottom-20 left-[5%] w-80 h-80 bg-secondary/5 rounded-full blur-3xl animate-blob-float-medium"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-secondary/10 border border-secondary/20 text-secondary rounded-full px-6 py-2 mb-4 text-xs font-bold uppercase tracking-wider shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-secondary" />
            <span>Testimoni Pengguna</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-4">
            Kata <span className="text-transparent bg-clip-text bg-gradient-hero">Gen Z Indonesia</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base">
            Dengar cerita mereka yang sudah merasakan manfaat nyata dari pendampingan MindfulMe
          </p>
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div className="grid md:grid-cols-3 gap-8">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="glass-card p-6 rounded-2xl border border-border/40 animate-pulse flex flex-col h-[280px]">
                <div className="h-4 bg-muted rounded w-1/3 mb-4" />
                <div className="h-20 bg-muted rounded mb-6" />
                <div className="flex items-center gap-3 mt-auto">
                  <div className="w-12 h-12 rounded-full bg-muted" />
                  <div className="h-4 bg-muted rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div className="text-center py-10 bg-red-50/50 rounded-2xl border border-red-100 max-w-md mx-auto">
            <p className="text-red-600 font-medium">
              Gagal memuat testimoni. Silakan coba lagi nanti.
            </p>
          </div>
        )}

        {/* Empty state (no approved testimonials yet) */}
        {/* {!loading && !error && testimonials.length === 0 && (
          <div className="text-center py-12 bg-muted/20 rounded-2xl border border-border max-w-md mx-auto">
            <p className="text-muted-foreground font-medium">
              Belum ada testimoni yang tersedia saat ini.
            </p>
          </div>
        )} */}

        {/* Testimonial cards */}
        <div className={`grid md:grid-cols-3 gap-8 ${loading || testimonials.length === 0 ? 'hidden' : ''}`}>
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.testimonial_id || index}
              className="group glass-card hover:glass-card-hover p-6 rounded-2xl border border-border/40 relative shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-500 flex flex-col h-full"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Quote Icon */}
              <div className="absolute -top-4 left-6 bg-gradient-hero w-9 h-9 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Quote className="w-4 h-4 text-white" />
              </div>

              {/* Stars */}
              <div className="flex gap-1 mb-5 pt-3">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-accent fill-accent drop-shadow-[0_0_4px_rgba(139,92,246,0.3)]" />
                ))}
              </div>

              {/* Media (image/video) */}
              {(() => {
                const url = testimonial.media_url;
                if (!url) return null;

                // Infer type: if media_type is set, use it; otherwise guess from URL extension
                const videoExts = /\.(mp4|mov|avi|mkv|webm)$/i;
                const isVideo =
                  testimonial.media_type === 'video' || videoExts.test(url);

                return isVideo ? (
                  <div className="mb-4 overflow-hidden rounded-xl border border-border/40 shadow-inner bg-black/5">
                    <video controls className="w-full h-48 object-cover">
                      <source src={url} />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                ) : (
                  <div className="mb-4 overflow-hidden rounded-xl border border-border/40 shadow-inner">
                    <img
                      src={url}
                      alt={testimonial.title || 'Testimoni'}
                      className="w-full h-48 object-cover group-hover:scale-[1.02] transition-transform duration-500"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                );
              })()}

              {/* Text */}
              <p className="text-foreground/90 mb-6 leading-relaxed italic text-sm flex-grow">
                "{testimonial.content}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3.5 mt-auto pt-4 border-t border-border/10 bg-white/20 p-2 rounded-xl">
                {testimonial.user?.photo ? (
                  <img
                    src={testimonial.user.photo}
                    alt={testimonial.user_name || 'User'}
                    className="w-11 h-11 rounded-full object-cover border-2 border-primary/20 shadow-sm"
                  />
                ) : (
                  <div
                    className={`w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0 shadow-sm ${getAvatarColor(testimonial.user_name || 'U')}`}
                  >
                    {getInitials(testimonial.user_name || 'U')}
                  </div>
                )}
                <div>
                  <h4 className="font-bold text-foreground text-sm">
                    {testimonial.user_name}
                  </h4>
                  {testimonial.title && (
                    <p className="text-xs text-muted-foreground font-semibold">
                      {testimonial.title}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="mt-24 text-center relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { val: "4.9/5", label: "Rating Aplikasi", color: "text-primary", bg: "bg-primary/5 border-primary/10" },
              { val: "95%", label: "User Satisfaction", color: "text-secondary", bg: "bg-secondary/5 border-secondary/10" },
              { val: "1000+", label: "Review Positif", color: "text-accent", bg: "bg-accent/5 border-accent/10" },
              { val: "24/7", label: "Support Layanan", color: "text-primary", bg: "bg-primary/5 border-primary/10" }
            ].map((stat, i) => (
              <div key={i} className={`p-6 rounded-2xl border ${stat.bg} backdrop-blur-sm shadow-sm hover:shadow-md hover:scale-[1.03] transition-all duration-300`}>
                <div className={`text-3xl font-extrabold ${stat.color} mb-2 tracking-tight`}>{stat.val}</div>
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground/80">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialSection;